import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { TeamsApiAdapter, type TenantChild } from '../adapters/TeamsApiAdapter';
import { useAuthStore } from '@shared/stores/authStore';

interface UseTenantChildrenResult {
  tenantChildren: TenantChild[];
  isTenant3pl: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useTenantChildren = (): UseTenantChildrenResult => {
  const team = useAuthStore((state) => state.team);
  const teamId = team?.id;
  const isTenant3pl = team?.type === 'tenant_3pl';

  const httpClient = createHttpClient(apiClientWH);
  const teamsAdapter = new TeamsApiAdapter(httpClient);

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tenant-children', teamId],
    queryFn: () => teamsAdapter.getTeamsChild(teamId),
    enabled: isTenant3pl && Boolean(teamId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const tenantChildren = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    if (!teamId) {
      return data;
    }

    return data.filter(
      (child) => child.parent_team_id === teamId
    );
  }, [data, teamId]);

  return {
    tenantChildren,
    isTenant3pl,
    isLoading,
    error: error instanceof Error ? error.message : null,
  };
};

